import { find, last } from 'lodash'

import { Page, StepperStep } from 'common/interfaces'

export interface RenderedStep {
  /** Stable key + the page `name` this step groups */
  name: string
  /** Display label */
  label: string
  /** Page id to navigate to when clicked, or null when the step isn't reachable yet */
  navigateId: string | null
  isCurrent: boolean
  isCompleted: boolean
}

/**
 * Builds the always-visible stepper from the flow's explicit `stepper_steps`.
 * A step is "current" when the current page shares its `name`, "completed" once
 * any visited page (in `path`) shares its `name`, and otherwise "upcoming".
 * `navigateId` is the first visited page with that `name` (so only reached steps
 * are clickable — matching the existing "only visited is clickable" rule).
 */
export function getFlowSteps(
  config: StepperStep[],
  path: string[],
  currentPage: Page,
  allPages: Page[]
): RenderedStep[] {
  const visitedPages = path.map((id) => find(allPages, { id })).filter(Boolean) as Page[]
  const visitedNames = new Set(visitedPages.map((page) => page.name))

  return config.map(({ name, label }) => {
    const isCurrent = currentPage.name === name
    const isCompleted = !isCurrent && visitedNames.has(name)
    const navigatePage = visitedPages.find((page) => page.name === name)

    return {
      name,
      label,
      navigateId: !isCurrent && navigatePage !== undefined ? navigatePage.id : null,
      isCurrent,
      isCompleted,
    }
  })
}

export function getSteps(path: string[], allSteps: Page[]): Page[] {
  const steps: Page[] = path.map((id) => find(allSteps, { id })!)

  let currentStep = last(steps)!
  while (currentStep.transitions.length === 1) {
    const nextStep = find(allSteps, { id: currentStep.transitions[0].destination })!
    steps.push(nextStep)
    currentStep = nextStep
  }

  const compressedSteps = [steps[0]]
  steps.forEach((step) => {
    if (last(compressedSteps)?.name !== step.name) {
      compressedSteps.push(step)
    }
  })

  return compressedSteps
}
